/* eslint-disable react-hooks/exhaustive-deps */
import {useAllShifts, useModel} from "hooks"
import React, {useMemo} from "react"
import {BaseNewFilter} from "uiKit"
import styles from "./DailyAttendanceFilter.module.css"
import {BaseDatePicker, BaseRangePicker} from "components/DateTimePicker"
import moment from "moment"
import {BaseButton, Icon} from "components"
import {ExportButton} from "components/ui"
import {Checkbox, Col, Radio, Row} from "antd"
import CampusSelect from "components/CampusSelect"
import TermSelect from "components/TermSelect"
import ProgramSelect from "components/ProgramSelect"
import ProgramVersionSelect from "components/ProgramVersionSelect"
import {NewStudentStatusSelectFilter} from "components/NewStudentStatusSelect"
import UserSelectV3 from "components/UserSelectV3"
import {Auth} from "types/auth"
import PhaseSelect from "components/PhaseSelect"
import ShiftSelect from "components/ShiftSelect"
import GroupSelect from "components/GroupSelect"
import {PermissionsRequired} from "components/PermissionsRequired"
import {Permissions} from "types/permission"
import {FilterKey} from "types/filter"

type DailyAttendanceFilterProps = {
    searchValue: string
    changeSearch: any
    onApplyFilter: any
    clearFilter: any
    currentFilter: any
    onOpenAddManualDailyAttendancePopup: any
    exporting: boolean
    handleExportFile: any
    onChangeFilter: any
    setCurrentFilter: any
    showDailyRoster?: boolean
    includeDateRange?: boolean
    onChangeDateRange?: any
    dateRange?: any[]
    date?: string
    onChangeDate?: any
    hideDateFilter?: boolean
    isMissingFameId?: boolean
    hasTriangleAlert?: boolean
    isErrorLog?: boolean
    enableMakeupFilter?: boolean
    enableShowDeletedAttendanceFilter?: boolean
    savedFilterOptions?: {
        enabled: boolean
        filterKey: FilterKey
        setFilter: (_filter: any) => void
    }
    showBatchUpdates?: boolean
    onOpenBatchDailyAttendanceUpdatesPopup?: () => void
}

export enum DailyAttendanceFilterCheckin {
    None = "None",
    HasCheckin = "HasCheckin",
    MissingCheckin = "MissingCheckin",
    LateCheckin = "LateCheckin"
}

export enum DailyAttendanceFilterCheckout {
    None = "None",
    HasCheckout = "HasCheckout",
    MissingCheckout = "MissingCheckout"
}

const DailyAttendanceFilter = (props: DailyAttendanceFilterProps) => {
    const {
        searchValue,
        changeSearch,
        onChangeDateRange,
        dateRange,
        date,
        onChangeDate,
        setCurrentFilter,
        onApplyFilter,
        clearFilter,
        currentFilter,
        onChangeFilter,
        showDailyRoster,
        onOpenAddManualDailyAttendancePopup,
        handleExportFile,
        enableShowDeletedAttendanceFilter,
        exporting,
        isErrorLog,
        enableMakeupFilter,
        savedFilterOptions,
        showBatchUpdates = false,
        onOpenBatchDailyAttendanceUpdatesPopup
    } = props
    const model = useModel()
    const dateFormat = model.getUserDateFormat()

    const {shifts} = useAllShifts()

    const allUserCampusIds = useMemo(() => {
        if (!model.isStaff()) {
            return []
        }
        const userCampuses = (model.user?.profiles || [])
            .map((profile) => (profile.campuses || []).map(({id}) => id))
            .filter(Boolean)
        return userCampuses.flat()
    }, [model.user])

    const customHeaderFilter = () => (
        <div className={styles.customHeaderFilter}>
            <Checkbox
                style={{marginLeft: 16}}
                checked={currentFilter?.isDailyRoster}
                onChange={(e) => {
                    const dayNumber = moment(date).day()

                    const shiftsFilter = shifts.filter((shift) => {
                        const workDays = shift.workWeek || []

                        return workDays.find((wd) => wd.dayOfWeek === dayNumber)
                    })
                    setCurrentFilter({
                        ...currentFilter,
                        isDailyRoster: e.target.checked,
                        shifts: e.target.checked ? shiftsFilter : []
                    })
                }}>
                Daily Roster
            </Checkbox>
        </div>
    )

    return model.isStaffOrAdmin() ? (
        <BaseNewFilter
            searchValue={searchValue}
            onSearchInput={changeSearch}
            onClick={onApplyFilter}
            onClickClear={clearFilter}
            filter={currentFilter}
            savedFilterOptions={savedFilterOptions}
            customHeaderFilter={showDailyRoster ? customHeaderFilter : undefined}
            renderRightFilter={() => (
                <div className={styles.rightFilter}>
                    {props.hideDateFilter ? null : props.includeDateRange ? (
                        <BaseRangePicker
                            placeholder={["Date From", "Date To"]}
                            value={dateRange?.length ? [moment(dateRange[0]), moment(dateRange[1])] : null}
                            onChange={(newValue) => {
                                onChangeDateRange(newValue)
                            }}
                        />
                    ) : (
                        <BaseDatePicker
                            className={styles.datePicker}
                            placeholder={dateFormat}
                            format={dateFormat}
                            value={date ? moment(date) : undefined}
                            onChange={(newValue) => {
                                onChangeDate(newValue.toISOString())

                                if (showDailyRoster && currentFilter?.isDailyRoster) {
                                    const dayNumber = moment(newValue.toISOString()).day()

                                    const shiftsFilter = shifts.filter((shift) => {
                                        const workDays = shift.workWeek || []

                                        return workDays.find((wd) => wd.dayOfWeek === dayNumber)
                                    })

                                    setCurrentFilter({
                                        ...currentFilter,
                                        shifts: shiftsFilter
                                    })
                                }
                            }}
                        />
                    )}
                    {!isErrorLog && (
                        <PermissionsRequired
                            permissions={{
                                staff: [Permissions.Staff.Academics.Registrar.Attendance.AllowAddManualAttendance.Add]
                            }}>
                            <BaseButton title="Add Manual" onClick={onOpenAddManualDailyAttendancePopup} />
                        </PermissionsRequired>
                    )}
                    {showBatchUpdates ? (
                        <PermissionsRequired
                            permissions={{
                                staff: [Permissions.Staff.Academics.Registrar.Attendance.AllowAddManualAttendance.Add]
                            }}>
                            <BaseButton
                                title=""
                                onClick={onOpenBatchDailyAttendanceUpdatesPopup}
                                icon={
                                    <Icon icon="TIME_CLOCK_OUTLINE" color="#FFF" className={styles.batchUpdatesIcon} />
                                }
                                className={styles.batchUpdatesButton}
                            />
                        </PermissionsRequired>
                    ) : null}
                    {!isErrorLog && (
                        <ExportButton
                            label="EXPORT"
                            isLoading={exporting}
                            onSelect={handleExportFile}
                            availableFileTypes={["csv", "excel"]}
                        />
                    )}
                </div>
            )}>
            <Row gutter={[40, 32]}>
                <Col span={12}>
                    <CampusSelect
                        isMulti
                        isClearable
                        placeholder="Campus"
                        displayOnlyCampusIds={allUserCampusIds}
                        value={currentFilter?.campuses}
                        onChange={(newValue) => {
                            const newFilter = {...currentFilter}
                            newFilter.campuses = newValue
                            setCurrentFilter(newFilter)
                        }}
                    />
                </Col>
                <Col span={12}>
                    <TermSelect
                        isMulti
                        isClearable
                        placeholder="Starting Terms"
                        value={currentFilter?.startingTerms}
                        onChange={(newValue) => onChangeFilter("startingTerms", newValue)}
                    />
                </Col>
                <Col span={12}>
                    <ProgramSelect
                        isMulti
                        isClearable
                        placeholder="Programs"
                        value={currentFilter?.programs}
                        onChange={(newValue) => {
                            const newFilter = {...currentFilter}
                            newFilter.programs = newValue
                            setCurrentFilter(newFilter)
                        }}
                    />
                </Col>
                <Col span={12}>
                    <ProgramVersionSelect
                        isMulti
                        isClearable
                        placeholder="Program Versions"
                        value={currentFilter?.programVersions}
                        onChange={(newValue) => onChangeFilter("programVersions", newValue)}
                    />
                </Col>

                <Col span={12}>
                    <NewStudentStatusSelectFilter
                        isMulti
                        isClearable
                        value={currentFilter?.studentStatuses}
                        onChange={(newValue) => onChangeFilter("studentStatuses", newValue)}
                    />
                </Col>

                <Col span={12}>
                    <UserSelectV3
                        isMulti
                        isClearable
                        type={[Auth.UserProfileType.Staff]}
                        value={currentFilter?.instructors}
                        belongsToMyCampuses={false}
                        onChange={(v) => onChangeFilter("instructors", v)}
                        placeholder={"Instructors"}
                    />
                </Col>

                <Col span={12}>
                    <PhaseSelect
                        isClearable
                        isMulti
                        value={currentFilter?.phases}
                        onChange={(newValue) => onChangeFilter("phases", newValue ?? null)}
                        placeholder="Phases"
                    />
                </Col>
                <Col span={12}>
                    <ShiftSelect
                        isClearable
                        isMulti
                        value={currentFilter?.shifts}
                        onChange={(newValue) => onChangeFilter("shifts", newValue ?? null)}
                        placeholder="Shifts"
                    />
                </Col>
                <Col span={12}>
                    <GroupSelect
                        isClearable
                        isMulti
                        value={currentFilter?.groups}
                        onChange={(newValue) => onChangeFilter("groups", newValue ?? null)}
                        placeholder="Groups"
                    />
                </Col>
                {!isErrorLog ? (
                    <>
                        <Col span={12} />
                        <Col span={12}>
                            <Checkbox
                                checked={currentFilter?.overlappingTime}
                                onChange={(event) => {
                                    onChangeFilter("overlappingTime", event.target.checked)
                                }}>
                                <span className={styles.radioButtonTitle}>Overlapping Punch</span>
                            </Checkbox>
                        </Col>
                    </>
                ) : null}
                {!isErrorLog && enableMakeupFilter ? (
                    <>
                        <Col span={12}>
                            <Checkbox
                                checked={currentFilter?.isMakeup}
                                onChange={(event) => {
                                    onChangeFilter("isMakeup", event.target.checked)
                                }}>
                                <span className={styles.radioButtonTitle}>Is Makeup</span>
                            </Checkbox>
                        </Col>
                        <Col span={12}>
                            <Checkbox
                                checked={currentFilter?.isMissingFameId}
                                onChange={(event) => {
                                    onChangeFilter("isMissingFameId", event.target.checked)
                                }}>
                                <span className={styles.radioButtonTitle}>Missing Fame ID</span>
                            </Checkbox>
                        </Col>

                        <Col span={12}>
                            <Checkbox
                                checked={currentFilter?.hasTriangleAlert}
                                onChange={(event) => {
                                    onChangeFilter("hasTriangleAlert", event.target.checked)
                                }}>
                                <span className={styles.radioButtonTitle}>Has Triangle Alert</span>
                            </Checkbox>
                        </Col>
                        <Col span={12}>
                            <Checkbox
                                checked={currentFilter?.isModified}
                                onChange={(event) => {
                                    onChangeFilter("isModified", event.target.checked)
                                }}>
                                <span className={styles.radioButtonTitle}>Is Modified</span>
                            </Checkbox>
                        </Col>
                    </>
                ) : null}

                {enableShowDeletedAttendanceFilter ? (
                    <Col span={12}>
                        <Checkbox
                            checked={currentFilter?.includeDeletedAttendance}
                            onChange={(event) => {
                                onChangeFilter("includeDeletedAttendance", event.target.checked)
                            }}>
                            <span className={styles.radioButtonTitle}>Include deleted attendance records</span>
                        </Checkbox>
                    </Col>
                ) : null}

                {!isErrorLog ? (
                    <>
                        <Col span={12}>
                            <Col span={24} style={{marginBottom: 15, paddingLeft: 0}}>
                                <span className={styles.radioButtonTitle}>Checkin</span>
                            </Col>
                            <Col span={24} style={{paddingLeft: 0}}>
                                <Radio.Group
                                    onChange={(event) => onChangeFilter("filterCheckin", event.target.value)}
                                    value={currentFilter?.filterCheckin}
                                    defaultValue={DailyAttendanceFilterCheckin.None}
                                    className={styles.radioBtnWrap}>
                                    <Radio value={DailyAttendanceFilterCheckin.None} className={styles.radioBtn__item}>
                                        None
                                    </Radio>
                                    <Radio
                                        value={DailyAttendanceFilterCheckin.HasCheckin}
                                        className={styles.radioBtn__item}>
                                        Has Checkin
                                    </Radio>
                                    <Radio
                                        value={DailyAttendanceFilterCheckin.MissingCheckin}
                                        className={styles.radioBtn__item}>
                                        Missing Checkin
                                    </Radio>
                                    <Radio
                                        value={DailyAttendanceFilterCheckin.LateCheckin}
                                        className={styles.radioBtn__item}>
                                        Late Checkin
                                    </Radio>
                                </Radio.Group>
                            </Col>
                        </Col>
                        <Col span={12}>
                            <Col span={24} style={{marginBottom: 15, paddingLeft: 0}}>
                                <span className={styles.radioButtonTitle}>Checkout</span>
                            </Col>
                            <Col span={24} style={{paddingLeft: 0}}>
                                <Radio.Group
                                    onChange={(event) => onChangeFilter("filterCheckout", event.target.value)}
                                    value={currentFilter?.filterCheckout}
                                    defaultValue={DailyAttendanceFilterCheckout.None}
                                    className={styles.radioBtnWrap}>
                                    <Radio value={DailyAttendanceFilterCheckout.None} className={styles.radioBtn__item}>
                                        None
                                    </Radio>
                                    <Radio
                                        value={DailyAttendanceFilterCheckout.HasCheckout}
                                        className={styles.radioBtn__item}>
                                        Has Checkout
                                    </Radio>
                                    <Radio
                                        value={DailyAttendanceFilterCheckout.MissingCheckout}
                                        className={styles.radioBtn__item}>
                                        Missing Checkout
                                    </Radio>
                                </Radio.Group>
                            </Col>
                        </Col>
                    </>
                ) : null}
            </Row>
        </BaseNewFilter>
    ) : null
}

export {DailyAttendanceFilter}
